import { Badge, Button, Page, Text } from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { ampli } from 'lib-shared-tracking';
import type { CardType } from 'lib-shared-types-api';
import uniqueId from 'lodash/uniqueId';
import { ChangeEvent, useCallback, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { validate as uuidValidate } from 'uuid';

import { integrationsApi } from 'apis/integrations';
import { articles } from 'utils/elevio';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { SettingsIntegrationsDocumentationButton } from '../../../../../components/documentationButton/documentationButton';
import type {
  AvailableParameter,
  Parameter,
} from '../cardsTest/cardsTest.types';
import { CardsTestResult } from '../cardsTestResult/cardsTestResult';

import styles from './cardsTest.module.scss';
import { CardsTestParameterTable } from './components/cardsTestParameterTable/cardsTestParameterTable';
import { getAvailableParameters } from './utils/parameters';
import { generateTestQueryString } from './utils/query';

export type CardsTestProps = {
  cardType: CardType;
};

export const CardsTest = ({ cardType }: CardsTestProps): JSX.Element => {
  const convertedParameters = getAvailableParameters(cardType);

  const navigate = useAppNavigate();
  const { id } = useAppPathParams(
    `/settings/integrations/${cardType}-card/test/:id`,
  );

  const [parameters, setParameters] = useState<Dictionary<Parameter>>({});
  const [hasWrongId, setHasWrongId] = useState<boolean>(false);
  const [availableParameters, setAvailableParameters] =
    useState<AvailableParameter[]>(convertedParameters);

  const [
    getCustomCardById,
    { data: customCard, isLoading: isLoadingCardById },
  ] = integrationsApi.useLazyReadCustomCardIntegrationByIdQuery();

  const [
    getCustomCardTest,
    { data: cardTestResult, isLoading: isTestLoading, isError: hasTestError },
  ] = integrationsApi.useLazyReadCustomCardTestIntegrationQuery();

  const title = customCard?.data?.title
    ? `Test: ${customCard.data.title}`
    : `Test: ${cardType} Card`;

  const isCardEmpty = hasWrongId || (!isLoadingCardById && title === '');

  useEffectOnce(() => {
    if (id && uuidValidate(id)) {
      getCustomCardById({ hookId: id });
    } else {
      setHasWrongId(true);
    }
  });

  const handleParameterSelect = useCallback(
    ({ id }: Parameter, key: string) => {
      setParameters({
        ...parameters,
        [key]: {
          ...parameters[key],
          id,
        },
      });
      setAvailableParameters([
        ...availableParameters.filter((parameter) => parameter.id !== id),
      ]);
    },
    [parameters, availableParameters],
  );

  const handleParameterChange = useCallback(
    (event: ChangeEvent<HTMLInputElement> | undefined) => {
      if (!event) {
        return;
      }
      event.preventDefault();
      const { name, value } = event.target;

      setParameters({
        ...parameters,
        [name]: {
          ...parameters[name],
          value,
        },
      });
    },
    [parameters],
  );

  const handleParameterDelete = useCallback(
    (key: string) => {
      const { [key]: parameterToDelete, ...remainingParameters } = parameters;

      setParameters(remainingParameters);
      setAvailableParameters(
        convertedParameters.filter(
          (parameter) =>
            !Object.values(remainingParameters).some(
              (item) => item.id === parameter.id,
            ),
        ),
      );

      if (id) {
        ampli.settingsIntegrationsCardTestRemoveParameter({
          cardType,
          cardId: id,
        });
      }
    },
    [parameters, convertedParameters, id, cardType],
  );

  const handleTest = useCallback(() => {
    const query = generateTestQueryString(parameters);

    if (id) {
      getCustomCardTest({ hookId: id, query });

      ampli.settingsIntegrationsCardTest({
        cardType,
        cardId: id,
      });
    }
  }, [cardType, parameters, id, getCustomCardTest]);

  if (isCardEmpty) {
    return (
      <Page.Section>
        <Text.Paragraph>
          This card does not exist. Please have a look at the existing{' '}
          <span
            onClick={() => navigate({ path: '/settings/integrations' })}
            className={styles.link}
          >
            cards here
          </span>
          .
        </Text.Paragraph>
      </Page.Section>
    );
  }

  return (
    <Page.Section
      title={title}
      titleMeta={
        <>
          {Object.keys(parameters).length > 0 && (
            <Badge size="small" variant="secondary">
              {Object.keys(parameters).length}
            </Badge>
          )}

          <SettingsIntegrationsDocumentationButton
            onClick={() => {
              ampli.settingsIntegrationsCardReadDocumentation({
                cardRoute: 'test',
                cardType,
              });

              elevioClient.openArticle(articles.settings.cards[cardType]);
            }}
          />
        </>
      }
      controls={
        <>
          <Button
            onClick={() => {
              if (id) {
                navigate({
                  path: `/settings/integrations/${cardType}-card/edit/:id`,
                  params: {
                    path: {
                      id,
                    },
                  },
                });
              }
            }}
            variant="secondary"
            disabled={isCardEmpty || isTestLoading}
          >
            Edit card
          </Button>

          <Button
            disabled={!availableParameters.length}
            variant="secondary"
            onClick={() => {
              setParameters({
                ...parameters,
                [uniqueId('parameter-')]: {
                  id: null,
                  value: '',
                },
              });

              if (id) {
                ampli.settingsIntegrationsCardTestAddParameter({
                  cardType,
                  cardId: id,
                });
              }
            }}
          >
            Add parameter
          </Button>

          <Button
            onClick={handleTest}
            isLoading={isTestLoading}
            disabled={isCardEmpty}
            data-testid="cards-test-button"
          >
            Test
          </Button>
        </>
      }
    >
      <CardsTestParameterTable
        parameters={parameters}
        availableParameters={availableParameters}
        handleParameterSelect={handleParameterSelect}
        handleParameterDelete={handleParameterDelete}
        handleParameterChange={handleParameterChange}
      />

      <CardsTestResult
        card={customCard?.data}
        isLoading={isTestLoading}
        hasFailed={hasTestError}
        testResult={cardTestResult?.data}
      />
    </Page.Section>
  );
};
